import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '../../lib/utils'

interface SelectOption {
  value: string
  label: string
  icon?: string
  iconNode?: React.ReactNode
  dot?: string // Tailwind bg class for color swatch, e.g. 'bg-blue-500'
}

interface SelectProps {
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
  size?: 'sm' | 'md' | 'lg'
  className?: string
  placeholder?: string
  borderColor?: string // Tailwind border color class, e.g. 'border-blue-500'
}

const Select: React.FC<SelectProps> = ({
  value,
  options,
  onChange,
  size = 'md',
  className,
  placeholder = 'Select option...',
  borderColor,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find((o) => o.value === value)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const sizeClasses = {
    // Same height as buttons (h-9) so dropdowns line up in toolbars
    sm: 'h-9 px-3 text-sm',
    md: 'h-9 px-3 text-sm',
    lg: 'h-9 px-4 text-sm',
  }

  const renderLabel = (option: SelectOption) => (
    <span className="flex items-center gap-2 min-w-0">
      {option.dot && (
        <span className={cn('w-3 h-3 rounded-full flex-shrink-0', option.dot)} />
      )}
      {option.iconNode
        ? <span className="flex-shrink-0">{option.iconNode}</span>
        : option.icon && <span className="flex-shrink-0">{option.icon}</span>
      }
      <span className="truncate">{option.label}</span>
    </span>
  )

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          'flex items-center justify-between gap-2 w-full bg-white dark:bg-gray-700 border-solid border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 text-left text-gray-900 dark:text-white',
          sizeClasses[size],
          borderColor,
          className
        )}
      >
        {selectedOption ? renderLabel(selectedOption) : (
          <span className="text-gray-400 truncate">{placeholder}</span>
        )}
        <ChevronDown
          className={cn(
            'w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl overflow-hidden">
          <div className="max-h-52 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => { onChange(option.value); setIsOpen(false) }}
                className={cn(
                  'w-full text-left px-4 py-2 text-sm transition-colors duration-150',
                  option.value === value
                    ? 'bg-brand-soft text-primary-700 dark:text-primary-300 font-medium'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/60'
                )}
              >
                {renderLabel(option)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default Select
